/**
 * The ZINQ API as the merchant panel uses it (M4 contract). Local types on purpose: the panel is
 * coded against the documented contract (docs/adr/0003-m4-panel-research-l10n.md and the M4 API
 * contract) instead of importing packages/contracts. Money is ALWAYS integer minor units (deni).
 * Errors are RFC 7807 problem+json with a machine-readable `code`.
 */

// ---------- errors ----------

export interface Problem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  errors?: unknown;
  code?: string;
}

/** Codes the panel branches on (others are shown with a generic message). */
export type PanelErrorCode =
  | 'PANEL_REQUIRED'
  | 'DATE_INVALID'
  | 'DATE_RANGE_TOO_LONG'
  | 'MERCHANT_NOT_FOUND'
  | 'RESEARCH_PIN_INVALID'
  | 'QR_AMOUNT_TOO_SMALL';

// ---------- auth ----------

export interface OtpRequestResponse {
  requestId: string;
  expiresInSec: number;
  /** Development only (the API never sends it in production). */
  devCode?: string;
  /**
   * M5 pilot OTP mode (no SMS provider in the pilot): the code itself, only for the study's
   * prepared test numbers. Shown on the sign-in screen with one-tap fill.
   */
  pilotCode?: string;
}

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  tokenType: 'Bearer';
  accessTokenExpiresInSec: number;
}

export interface Me {
  id: string;
  displayName: string;
  phone: string;
  phoneMasked: string;
  kycStatus: string;
  hasSendAccount: boolean;
  hasReceiveAccount: boolean;
  plusActive: boolean;
}

// ---------- merchant ----------

/** 'kb' | 'stb' | 'nlb' | 'halk' | 'spk' | 'pcb' | 'mock' — kept open for new banks. */
export type BankId = string;

export interface MerchantReceiveAccount {
  id: string;
  bankId: BankId;
  bankName: string;
  maskedNumber: string;
  active: boolean;
}

export interface Merchant {
  id: string;
  /** ZINQ merchant code, e.g. 'ZQM180723' (printed on the counter QR). */
  code: string;
  displayName: string;
  city: string;
  mcc: string;
  receiveAccount: MerchantReceiveAccount;
  status: 'ACTIVE' | 'SUSPENDED';
  createdAt: string;
}

/** GET /merchants/me/panel — the 350 ден./month web panel subscription. */
export interface PanelStatus {
  active: boolean;
  activatedAt: string | null;
  cancelledAt: string | null;
  priceMinor: number;
  currency: string;
  /** 'YYYY-MM' of the last month billed. */
  lastBilledMonth: string | null;
  /** Every billed month, 'YYYY-MM'. */
  billedMonths: string[];
}

export type PaymentState =
  | 'CREATED'
  | 'AWAITING_AUTHORIZATION'
  | 'AUTHORIZED'
  | 'SUBMITTED'
  | 'SETTLED'
  | 'REJECTED'
  | 'FAILED'
  | 'EXPIRED'
  | 'CANCELLED';

export type QrIntentKind = 'STATIC' | 'DYNAMIC';

/** One QR payment as the merchant sees it (payer in privacy form 'Ana P.'). */
export interface MerchantPayment {
  paymentId: string;
  reference: string;
  kind: QrIntentKind;
  state: PaymentState;
  amountMinor: number;
  mdrMinor: number;
  netMinor: number;
  payerName: string;
  createdAt: string;
  settledAt: string | null;
}

export interface MerchantPaymentList {
  items: MerchantPayment[];
  nextCursor: string | null;
}

/** Totals over SETTLED QR payments of a day (?date=) or a range (?from=&to=). */
export interface MerchantSummary {
  date?: string;
  from?: string;
  to?: string;
  count: number;
  grossMinor: number;
  mdrMinor: number;
  netMinor: number;
  currency: string;
}

/** A day (?date=) or an inclusive range of Europe/Skopje days (?from=&to=, max 92 days). */
export type DayRange = { date: string } | { from: string; to: string };

// ---------- reconciliation ----------

export type ReconciliationStatus =
  | 'MATCHED'
  | 'PENDING'
  | 'MISSING_IN_BANK'
  | 'AMOUNT_MISMATCH'
  | 'OTHER_INCOMING';

export const RECONCILIATION_STATUSES: readonly ReconciliationStatus[] = [
  'MATCHED',
  'PENDING',
  'MISSING_IN_BANK',
  'AMOUNT_MISMATCH',
  'OTHER_INCOMING',
];

export type ReconciliationUnavailableReason = 'MOCK_RAILS' | 'NO_CONSENT' | 'BANK_ERROR';

export interface ReconciliationAccount {
  id: string;
  bankId: BankId;
  bankName: string;
  maskedNumber: string;
}

export interface ReconciliationSummary {
  zinqSettledCount: number;
  zinqSettledMinor: number;
  matchedCount: number;
  matchedMinor: number;
  pendingCount: number;
  missingInBankCount: number;
  amountMismatchCount: number;
  otherIncomingCount: number;
  otherIncomingMinor: number;
}

export interface ReconciliationRow {
  status: ReconciliationStatus;
  paymentId: string | null;
  reference: string | null;
  endToEndId: string | null;
  payerName: string | null;
  zinqAmountMinor: number | null;
  bankAmountMinor: number | null;
  mdrMinor: number | null;
  netMinor: number | null;
  zinqSettledAt: string | null;
  bankBookedAt: string | null;
  bankLineId: string | null;
  /** The other party on the merchant's own bank statement (OTHER_INCOMING). */
  counterpartyName: string | null;
}

/** GET /merchants/me/reconciliation?from&to (max 31 days; panel plan required). */
export interface Reconciliation {
  from: string;
  to: string;
  available: boolean;
  unavailableReason: ReconciliationUnavailableReason | null;
  account: ReconciliationAccount | null;
  summary: ReconciliationSummary;
  rows: ReconciliationRow[];
}

// ---------- billing ----------

export type InvoiceLineKind = 'P2P_FEE' | 'PLUS_SUB' | 'MERCHANT_MDR' | 'PANEL_SUB';

export interface InvoiceLine {
  kind: InvoiceLineKind;
  count: number;
  amountMinor: number;
}

export interface InvoiceItem {
  id: string;
  kind: InvoiceLineKind;
  amountMinor: number;
  paymentId: string | null;
  reference: string | null;
  createdAt: string;
}

export type InvoiceStatus = 'OPEN' | 'CLOSED';

/** GET /billing/invoice?month=YYYY-MM */
export interface Invoice {
  month: string;
  currency: string;
  status: InvoiceStatus;
  lines: InvoiceLine[];
  /** Newest first, at most 500. */
  items: InvoiceItem[];
  totalMinor: number;
}

/** GET /billing/months (newest first). */
export interface BillingMonth {
  month: string;
  totalMinor: number;
  status: InvoiceStatus;
}

// ---------- QR ----------

export type QrIntentStatus = 'OPEN' | 'PAID' | 'EXPIRED' | 'CANCELLED';

export interface QrIntent {
  id: string;
  kind: QrIntentKind;
  reference: string;
  amountMinor: number | null;
  status: QrIntentStatus;
  inProgress: boolean;
  expiresAt: string | null;
  paidPaymentId: string | null;
  note: string | null;
  createdAt: string;
}

/** GET /merchants/me/static-qr — the counter QR (payer enters the amount). */
export interface StaticQr {
  intent: QrIntent;
  /** EMVCo MPM string to render as a QR code. */
  payload: string;
  /** Smallest amount a customer can pay (QR_MIN_AMOUNT_MINOR, default 5000 = 50 ден.). */
  minAmountMinor?: number;
}

// ---------- real-time events (GET /events/stream) ----------

export interface ReadyEvent {
  serverTime: string;
  bootId: string;
  heartbeatMs: number;
}

export interface PaymentReceivedEvent {
  paymentId: string;
  kind: 'P2P' | 'MERCHANT';
  amountMinor: number;
  currency: string;
  counterpartyName: string;
  merchantId: string | null;
  netMinor: number | null;
  occurredAt: string;
}

export interface QrIntentUpdatedEvent {
  intentId: string;
  reference: string;
  kind: QrIntentKind;
  status: QrIntentStatus;
  inProgress: boolean;
  paymentId: string | null;
  amountMinor: number | null;
  mdrMinor: number | null;
  netMinor: number | null;
  payerName: string | null;
  occurredAt: string;
}

export interface LiveEventMap {
  ready: ReadyEvent;
  resync: Record<string, never>;
  'payment.received': PaymentReceivedEvent;
  'qr_intent.updated': QrIntentUpdatedEvent;
  'payment.updated': Record<string, unknown>;
  'notification.created': Record<string, unknown>;
}

export type LiveEventType = keyof LiveEventMap;

// ---------- research (x-research-key) ----------

export type ResearchRole = 'USER' | 'MERCHANT';

export interface ResearchMetricValue {
  /** Catalog metric id: H1, H3, H5, H6_fee, H6_plus, SUS. */
  id: string;
  /** The role the metric is computed for (SUS appears once per role). */
  role?: ResearchRole;
  /** A share 0..1 for hypothesis metrics; the mean SUS score 0..100 for SUS; null without data. */
  value: number | null;
  /** Sessions the value is computed over. */
  n: number;
  threshold: number | null;
  pass: boolean | null;
}

export interface ResearchTaskStat {
  taskId: string;
  n: number;
  /** 0..1, null without data. */
  successRate: number | null;
  medianSeconds: number | null;
  p90Seconds: number | null;
}

/** A role's mean SUS score (0..100); null without a complete questionnaire. */
export type ResearchSusValue = number | null;

/** GET /research/summary?studyLabel= */
export interface ResearchSummary {
  studyLabel: string;
  sessions: Record<ResearchRole, number>;
  metrics: ResearchMetricValue[];
  tasks: ResearchTaskStat[];
  sus: Record<ResearchRole, ResearchSusValue>;
}

export type ResearchExport = 'sessions' | 'tasks' | 'answers' | 'events';
